"use client"

import * as React from "react"
import {
  createContext,
  use,
  useCallback,
  useRef,
  type ComponentProps,
  type HTMLAttributes,
} from "react"

import { cn } from "@/lib/utils"
import { Progress } from "@/components/ui/progress"

function formatTimestamp(value: number) {
  if (!Number.isFinite(value) || value < 0) return "0:00"
  const totalSeconds = Math.floor(value)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

interface ScrubBarContextValue {
  duration: number
  value: number
  progress: number
  onScrub?: (time: number) => void
  onScrubStart?: () => void
  onScrubEnd?: () => void
}

const ScrubBarContext = createContext<ScrubBarContextValue | null>(null)

function useScrubBarContext() {
  const context = use(ScrubBarContext)
  if (!context) {
    throw new Error("useScrubBarContext must be used within a ScrubBar.Root")
  }
  return context
}

interface ScrubBarContainerProps extends HTMLAttributes<HTMLDivElement> {
  duration: number
  value: number
  onScrub?: (time: number) => void
  onScrubStart?: () => void
  onScrubEnd?: () => void
}

function ScrubBarContainer({
  duration,
  value,
  onScrub,
  onScrubStart,
  onScrubEnd,
  children,
  className,
  ...props
}: ScrubBarContainerProps) {
  const progress = duration > 0 ? (value / duration) * 100 : 0

  const contextValue: ScrubBarContextValue = {
    duration,
    value,
    progress,
    onScrub,
    onScrubStart,
    onScrubEnd,
  }

  return (
    <ScrubBarContext.Provider value={contextValue}>
      <div
        data-slot="scrub-bar-root"
        className={cn("flex w-full items-center", className)}
        {...props}
      >
        {children}
      </div>
    </ScrubBarContext.Provider>
  )
}
ScrubBarContainer.displayName = "ScrubBarContainer"

type ScrubBarTrackProps = HTMLAttributes<HTMLDivElement>

function ScrubBarTrack({ className, children, ...props }: ScrubBarTrackProps) {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const { duration, onScrub, onScrubStart, onScrubEnd, value } =
    useScrubBarContext()

  // Keyboard operability for the slider (TES-839). A named, focusable slider
  // is required for `aria-input-field-name` and makes the scrubber usable
  // without a pointer. Pointer logic above is untouched.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!duration || !onScrub) return
      const arrowStep = Math.max(0.5, duration / 20)
      const pageStep = Math.max(1, duration / 5)
      let next: number | null = null
      switch (event.key) {
        case "ArrowRight":
        case "ArrowUp":
          next = Math.min(duration, value + arrowStep)
          break
        case "ArrowLeft":
        case "ArrowDown":
          next = Math.max(0, value - arrowStep)
          break
        case "PageUp":
          next = Math.min(duration, value + pageStep)
          break
        case "PageDown":
          next = Math.max(0, value - pageStep)
          break
        case "Home":
          next = 0
          break
        case "End":
          next = duration
          break
      }
      if (next != null) {
        event.preventDefault()
        onScrubStart?.()
        onScrub(next)
        onScrubEnd?.()
      }
    },
    [duration, onScrub, onScrubStart, onScrubEnd, value]
  )

  const getTimeFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current
      if (!track || !duration) return null
      const rect = track.getBoundingClientRect()
      const ratio = (clientX - rect.left) / rect.width
      const clamped = Math.min(Math.max(ratio, 0), 1)
      return duration * clamped
    },
    [duration]
  )

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!duration) return
      event.preventDefault()
      onScrubStart?.()
      const time = getTimeFromClientX(event.clientX)
      if (time != null) {
        onScrub?.(time)
      }

      const handleMove = (moveEvent: PointerEvent) => {
        const nextTime = getTimeFromClientX(moveEvent.clientX)
        if (nextTime != null) {
          onScrub?.(nextTime)
        }
      }

      const handleUp = () => {
        onScrubEnd?.()
        window.removeEventListener("pointermove", handleMove)
        window.removeEventListener("pointerup", handleUp)
      }

      window.addEventListener("pointermove", handleMove)
      window.addEventListener("pointerup", handleUp, { once: true })
    },
    [duration, getTimeFromClientX, onScrub, onScrubEnd, onScrubStart]
  )

  const clampedValue = Math.min(Math.max(value, 0), duration || 0)

  return (
    <div
      ref={trackRef}
      data-slot="scrub-bar-track"
      className={cn(
        "relative h-2 w-full grow cursor-pointer touch-none select-none rounded-[var(--radius-pill)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-hover)] transition-none [container-type:inline-size]",
        // WCAG 2.5.5: the global coarse-pointer min-height rule excludes
        // [role="slider"] so the visible track stays h-2; this invisible
        // ::before overlay restores the 44px touch hit area. Pointer events
        // on it bubble to the track's own onPointerDown handler.
        "before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2",
        className
      )}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      // eslint-disable-next-line react-doctor/prefer-tag-over-role -- custom pointer-event scrub track with child Progress; <input type=range> cannot contain children
      role="slider"
      aria-label="Audio seek slider"
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={duration || 0}
      aria-valuenow={clampedValue}
      aria-valuetext={`${formatTimestamp(clampedValue)} of ${formatTimestamp(
        duration || 0
      )}`}
      {...props}
    >
      {children}
    </div>
  )
}
ScrubBarTrack.displayName = "ScrubBarTrack"

type ScrubBarProgressProps = Omit<ComponentProps<typeof Progress>, "value">

function ScrubBarProgress({ className, ...props }: ScrubBarProgressProps) {
  const { progress } = useScrubBarContext()

  return (
    <Progress
      data-slot="scrub-bar-progress"
      value={progress}
      // TES-839: this is a decorative fill inside the role="slider" track,
      // which already exposes value to AT. Hiding the redundant nested
      // role="progressbar" prevents an unnamed-progressbar a11y failure.
      aria-hidden="true"
      className={cn("absolute h-full [&>div]:transition-none", className)}
      {...props}
    />
  )
}
ScrubBarProgress.displayName = "ScrubBarProgress"

type ScrubBarThumbProps = HTMLAttributes<HTMLDivElement>

function ScrubBarThumb({ className, children, ...props }: ScrubBarThumbProps) {
  const { progress } = useScrubBarContext()
  return (
    <div
      data-slot="scrub-bar-thumb"
      className={cn(
        "absolute left-0 top-1/2 block size-4 rounded-[var(--radius-pill)] border-2 border-[var(--tls-surface)] bg-[var(--tls-brand)] transition-colors disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      style={{
        transform: `translate3d(${progress}cqw, -50%, 0) translateX(-50%)`,
      }}
      {...props}
    >
      {children}
    </div>
  )
}
ScrubBarThumb.displayName = "ScrubBarThumb"

interface ScrubBarTimeLabelProps extends HTMLAttributes<HTMLSpanElement> {
  time: number
  format?: (time: number) => string
}

function ScrubBarTimeLabel({
  className,
  time,
  format = formatTimestamp,
  ...props
}: ScrubBarTimeLabelProps) {
  return (
    <span
      data-slot="scrub-bar-time-label"
      {...props}
      className={cn(
        // DS type-meta: 12.5px / 600 / tabular. shrink-0 + nowrap because the
        // track beside it is `w-full grow`, which otherwise squeezes the label
        // to zero width and wraps "0:00" onto two lines.
        "shrink-0 whitespace-nowrap font-semibold tabular-nums text-[0.78125rem] text-[var(--tls-fg-muted)]",
        className
      )}
    >
      {format(time)}
    </span>
  )
}
ScrubBarTimeLabel.displayName = "ScrubBarTimeLabel"

export {
  ScrubBarContainer,
  ScrubBarTrack,
  ScrubBarProgress,
  ScrubBarThumb,
  ScrubBarTimeLabel,
}
